/**
 * logActivitySheet.ts — the Log Activity sheet's pure rules (Log Activity
 * slice A, spec §3.1 as corrected by the recon; RULED 2026-10-07). Nothing
 * here fetches and nothing here invents a field: the deal pick reads what ONE
 * GET /v1/sales/accounts/{id} returns for the picked contact's account, and
 * the subject chips read the picked contact.
 *
 *   - DEAL: exactly one open deal at the account → that deal, tagged "Only
 *     open deal at account". Several → the most recently CREATED (the New
 *     Deal sheet's mostRecentOpen — the deals carry no activity stamp, so the
 *     spec's "most recently active" has nothing honest to read), tagged with
 *     how many others are open. None → no pick.
 *   - SUBJECT CHIPS: three per type, from the contact's name and company. A
 *     chip that needs a company is left out when the contact has none. The
 *     Call and Task templates are the spec's (§3.1); the Email, Meeting and
 *     Note templates are OURS — the spec gives none.
 */
import type { SalesActivity, SalesContact, SalesDeal } from '../../types'
import { mostRecentOpen } from './newDealAutofill'
import { readDraft, writeDraft } from '../../../../lib/draftStorage'

export const TAG_ONLY_OPEN_DEAL = 'Only open deal at account'

export interface DealPick { dealId: string; tag: string }

export function pickDeal(openDeals: readonly SalesDeal[]): DealPick | null {
  if (openDeals.length === 1) return { dealId: openDeals[0].id, tag: TAG_ONLY_OPEN_DEAL }
  const recent = mostRecentOpen(openDeals)
  if (!recent) return null
  return { dealId: recent.deal.id, tag: `and ${recent.more} more open ${recent.more === 1 ? 'deal' : 'deals'} at account` }
}

type Template = (name: string, company: string) => string | null
const withCompany = (f: (company: string) => string): Template => (_n, c) => (c ? f(c) : null)
const withName = (f: (name: string) => string): Template => n => (n ? f(n) : null)

const SUBJECT_TEMPLATES: Record<SalesActivity['type'], readonly Template[]> = {
  // The spec's (§3.1).
  call: [withCompany(c => `Discovery call — ${c}`), withName(n => `Call with ${n}`), withCompany(c => `Pricing call — ${c}`)],
  task: [withName(n => `Send recap to ${n}`), withCompany(c => `Prepare proposal — ${c}`), withName(n => `Book meeting with ${n}`)],
  // Ours: the spec names templates for Call and Task only.
  email: [withName(n => `Follow-up email to ${n}`), withCompany(c => `Proposal sent — ${c}`), withCompany(c => `Intro email — ${c}`)],
  meeting: [withName(n => `Meeting with ${n}`), withCompany(c => `Product demo — ${c}`), withCompany(c => `Kick-off meeting — ${c}`)],
  note: [withName(n => `Notes from call with ${n}`), withCompany(c => `Budget and timing — ${c}`), withCompany(c => `Next steps — ${c}`)],
}

/** Up to three subject suggestions; none until a contact is picked. */
export function subjectChips(type: SalesActivity['type'], contact: Pick<SalesContact, 'name' | 'company'> | null): string[] {
  if (!contact) return []
  const name = contact.name.trim()
  const company = contact.company.trim()
  return SUBJECT_TEMPLATES[type].map(t => t(name, company)).filter((s): s is string => s !== null)
}

/** The contact card's second line: only the parts the record has. */
export function contactLine(contact: Pick<SalesContact, 'title' | 'company' | 'email'>): string {
  return [contact.title, contact.company, contact.email].map(s => (s ?? '').trim()).filter(Boolean).join(' · ')
}

// ── Slice B (spec §3.1–§3.3, RULED 2026-10-08) ───────────────────────────────
// When, duration, outcome, the open-deal search, the last type used, inline
// validation and the ⌘↵ guard. Dates are LOCAL calendar days; "today" is
// always passed in, never read here.

type ActivityType = SalesActivity['type']

/** A logged type's When: Just now, a past day (R7 — today's backdating, kept
 *  until slice D's Earlier today / Yesterday), or a future slot. A task's Due:
 *  Today, the next working day, a week out, or a picked day. */
export type WhenChoice = 'now' | 'pick' | 'schedule' | 'today' | 'next' | 'week'

const LOGGED_WHEN = [
  { value: 'now', label: 'Just now' },
  { value: 'pick', label: 'Pick date' },
  { value: 'schedule', label: 'Schedule…' },
] as const satisfies readonly { value: WhenChoice; label: string }[]
const TASK_DUE = [
  { value: 'today', label: 'Today' },
  { value: 'next', label: 'Next working day' },
  { value: 'week', label: 'In a week' },
  { value: 'pick', label: 'Pick date' },
] as const satisfies readonly { value: WhenChoice; label: string }[]

/** The When (or, for a task, Due) chips. A note is never scheduled — today's
 *  sheet offers it no Schedule either. */
export function whenChoices(type: ActivityType): readonly { value: WhenChoice; label: string }[] {
  if (type === 'task') return TASK_DUE
  if (type === 'note') return LOGGED_WHEN.filter(c => c.value !== 'schedule')
  return LOGGED_WHEN
}

export function defaultWhen(type: ActivityType): WhenChoice {
  return type === 'task' ? 'next' : 'now'
}

/** 'YYYY-MM-DD' of a local day. */
export function isoDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const isWeekend = (d: Date) => d.getDay() === 0 || d.getDay() === 6

/** n working days after `from` (n ≥ 1). Weekends are skipped; bank holidays
 *  are not known here and are not skipped. */
export function addWorkingDays(from: Date, n: number): Date {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate())
  let left = n
  while (left > 0) {
    d.setDate(d.getDate() + 1)
    if (!isWeekend(d)) left--
  }
  return d
}

/** A task's due day for its chip; '' when Pick date has no day yet. */
export function resolveTaskDue(choice: WhenChoice, today: Date, picked: string): string {
  const day = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  switch (choice) {
    case 'today': return isoDay(day)
    case 'next': return isoDay(addWorkingDays(day, 1))
    case 'week': {
      day.setDate(day.getDate() + 7)
      return isoDay(isWeekend(day) ? addWorkingDays(day, 1) : day)
    }
    default: return picked
  }
}

const DAY = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Fri 2 Oct" for a 'YYYY-MM-DD' day. */
export function fmtDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  return `${DAY[date.getDay()]} ${date.getDate()} ${MONTH[date.getMonth()]}`
}

/** The resolved date shown on the right of When / Due; '' when there is none. */
export function resolvedWhen({ type, when, date, time, now }: {
  type: ActivityType; when: WhenChoice; date: string; time: string; now: Date
}): string {
  if (type === 'task') {
    const day = resolveTaskDue(when, now, date)
    return day ? fmtDay(day) + (when === 'pick' && time ? `, ${time}` : '') : ''
  }
  if (when === 'now') {
    const p = (n: number) => String(n).padStart(2, '0')
    return `${fmtDay(isoDay(now))}, ${p(now.getHours())}:${p(now.getMinutes())}`
  }
  return date ? fmtDay(date) + (time ? `, ${time}` : '') : ''
}

/** Whether the sheet is SCHEDULING: Outcome hidden, "Schedule {type}". */
export const isScheduling = (type: ActivityType, when: WhenChoice) => type !== 'task' && when === 'schedule'

/** The primary button. Never a type colour (slice A): the text alone changes. */
export function primaryLabel(type: ActivityType, when: WhenChoice): string {
  if (type === 'task') return 'Create task'
  return `${isScheduling(type, when) ? 'Schedule' : 'Log'} ${type}`
}

/** Only a logged (not scheduled) non-task is completed after its create. */
export function completesAfterCreate(type: ActivityType, when: WhenChoice): boolean {
  return type !== 'task' && !isScheduling(type, when)
}

/** The spec's durations (R3: none preselected — a duration is written only
 *  when the rep picks one). Old rows keep "1 hour" / "1.5 hrs" / "2 hrs". */
export const DURATIONS = ['15 min', '30 min', '45 min', '1 hr', '1.5 hr', '2 hr'] as const

export type OutcomeTone = 'good' | 'retry' | 'bad' | 'neutral'
export interface OutcomeOption { label: string; tone: OutcomeTone }

const OUTCOMES: Partial<Record<ActivityType, readonly OutcomeOption[]>> = {
  // The spec's, in its order (§3.1).
  call: [
    { label: 'Connected', tone: 'good' }, { label: 'No answer', tone: 'retry' },
    { label: 'Left voicemail', tone: 'retry' }, { label: 'Busy', tone: 'retry' },
    { label: 'Wrong number', tone: 'bad' },
  ],
  // The spec's words (R4); rows logged before keep Completed / No show /
  // Follow-up needed.
  meeting: [
    { label: 'Held', tone: 'good' }, { label: 'No-show', tone: 'bad' },
    { label: 'Rescheduled', tone: 'retry' }, { label: 'Cancelled', tone: 'bad' },
  ],
  // Today's list and encoding (R5); the dots are OUR choice — the spec gives
  // email no outcomes.
  email: [
    { label: 'Sent', tone: 'neutral' }, { label: 'Replied', tone: 'good' },
    { label: 'Bounced', tone: 'bad' }, { label: 'Opened', tone: 'neutral' },
    { label: 'No response', tone: 'retry' },
  ],
}

export function outcomesFor(type: ActivityType): readonly OutcomeOption[] {
  return OUTCOMES[type] ?? []
}

/** The OPEN stages (the 0097 vocabulary minus the two closed ones): the deal
 *  search asks each once, as the Reports open drill does (R1). */
export const OPEN_DEAL_STAGES = ['new_lead', 'qualified', 'proposal_sent', 'negotiation'] as const

/** The per-stage results as ONE list: de-duplicated by id, by name then id. */
export function mergeOpenDeals(lists: readonly (readonly SalesDeal[])[]): SalesDeal[] {
  const byId = new Map<string, SalesDeal>()
  for (const list of lists) for (const d of list) if (!byId.has(d.id)) byId.set(d.id, d)
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
}

// The last type used, per tenant and user (spec §3.0). The key carries the
// /v1/me identity; without one, nothing is read or written. Every access goes
// through draftStorage's guard, so storage that throws reads as "Call".
const TYPES: readonly ActivityType[] = ['call', 'email', 'meeting', 'note', 'task']

export function lastTypeKey(identity: { tenantId: string; subject: string } | null): string | null {
  return identity ? `gss.pref.logActivityType.${identity.tenantId}.${identity.subject}` : null
}

export function readLastType(key: string | null): ActivityType {
  if (!key) return 'call'
  const v = readDraft<unknown>(key)
  return TYPES.includes(v as ActivityType) ? (v as ActivityType) : 'call'
}

export function writeLastType(key: string | null, type: ActivityType): void {
  if (key) writeDraft(key, type)
}

export interface LogFieldErrors { contact?: string; subject?: string; due?: string }

/** Inline validation (spec §3.3): Contact and Subject, plus the day that
 *  When / Due needs. Pick date is for the past — a future day is a schedule. */
export function validateLog({ type, when, contactId, subject, date, today }: {
  type: ActivityType; when: WhenChoice; contactId: string; subject: string; date: string; today: Date
}): LogFieldErrors {
  const e: LogFieldErrors = {}
  if (!contactId) e.contact = 'Pick a contact from the list'
  if (!subject.trim()) e.subject = 'Enter a subject'
  if (type === 'task') {
    if (when === 'pick' && !date) e.due = 'Pick a due date'
  } else if (when === 'pick') {
    if (!date) e.due = 'Pick the day it happened'
    else if (date > isoDay(today)) e.due = 'That day is in the future — use Schedule… instead'
  } else if (when === 'schedule' && !date) {
    e.due = 'Pick a date to schedule it for'
  }
  return e
}

/** ⌘↵ / Ctrl+↵ only. */
export function isSubmitChord(e: { key: string; metaKey: boolean; ctrlKey: boolean }): boolean {
  return e.key === 'Enter' && (e.metaKey || e.ctrlKey)
}

/** Whether a key event belongs to THIS sheet and not to a dialog nested in it
 *  (a portal's events bubble through the React tree to the sheet). */
export function ownsKeyEvent(sheet: Element, target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('[role="dialog"]') === sheet
}

// ── Slice C1: the pre-fill baseline (RULED R8) ────────────────────────────────

/** What the sheet opened with. A pre-filled contact or deal is the CLEAN
 *  state: an immediate close asks nothing. Empty for the Activities button. */
export interface PrefillBaseline { contactSearch: string; dealId: string }

export function prefillBaseline(prefill: { contact?: { name: string } | null; deal?: { id: string } | null }): PrefillBaseline {
  return { contactSearch: prefill.contact?.name ?? '', dealId: prefill.deal?.id ?? '' }
}

/** "Touched" = a content field differs from what the sheet opened with. The
 *  type and the When chip are excluded: each is one click to re-pick. A deal
 *  still carrying its autofill tag was derived from the contact, so it counts
 *  exactly when the contact does — never on its own (with an empty baseline
 *  this is the slice B rule unchanged: an autofill only follows a picked
 *  contact, which already counts). */
export function isTouched(f: {
  description: string; contactSearch: string; notes: string; date: string; time: string
  duration: string; outcome: string; dealId: string; dealTag: string | null; dealSearch: string
}, base: PrefillBaseline): boolean {
  const contactChanged = f.contactSearch !== base.contactSearch
  const dealChanged = f.dealId !== base.dealId && (f.dealTag === null || contactChanged)
  return f.description !== '' || contactChanged || f.notes !== '' || f.date !== '' ||
    f.time !== '' || f.duration !== '' || f.outcome !== '' || dealChanged || f.dealSearch !== ''
}
